import { useState } from 'react';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent } from '@shelflix/ui-web/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shelflix/ui-web/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';

interface CampaignDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  canDelete: boolean;
  dependencies?: {
    hasDefaultMaterialDependency: boolean;
    hasCurrentMaterialDependency: boolean;
    defaultMaterials: Array<{ uuid: string; name: string }>;
    currentMaterials: Array<{ uuid: string; name: string }>;
  };
  isLoading?: boolean;
  // New props for batch update
  availableCampaigns?: Array<{ uuid: string; name: string }>;
  onUpdateAndDelete?: (newDefaultCampaignUuid: string) => void;
  isLoadingCampaigns?: boolean;
}

export function CampaignDeletionModal({
  isOpen,
  onClose,
  onConfirm,
  canDelete,
  dependencies,
  isLoading = false,
  availableCampaigns = [],
  onUpdateAndDelete,
  isLoadingCampaigns = false,
}: CampaignDeletionModalProps) {
  const [selectedCampaignUuid, setSelectedCampaignUuid] = useState<string>('');

  // Render warning modal (cannot delete)
  const renderWarningModal = () => {
    if (!dependencies) return null;

    const defaultMaterials = dependencies.defaultMaterials || [];
    const currentMaterials = dependencies.currentMaterials || [];

    return (
      <Dialog key="warning-modal" open={isOpen} onOpenChange={onClose}>
        <DialogContent className="flex max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:max-h-[85vh] sm:max-w-5xl">
          <div className="flex-shrink-0 p-6 pb-4">
            <DialogHeader className="text-left">
              <DialogTitle className="text-[var(--primary)]">Não é possível excluir a campanha</DialogTitle>
              <DialogDescription>
                Esta campanha está vinculada como <strong>campanha padrão</strong> de materiais ativos e não pode ser
                removida no momento. Para prosseguir com a exclusão, é necessário primeiro alterar a campanha padrão
                desses materiais para outra campanha.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-4">
            {defaultMaterials.length > 0 && (
              <div className="mb-4">
                <h4 className="mb-2 text-sm font-semibold text-[var(--primary)]">
                  Materiais problemáticos ({defaultMaterials.length}):
                </h4>
                <ul className="space-y-2">
                  {defaultMaterials.map((material) => (
                    <li key={material.uuid} className="flex items-center gap-2 pl-4 text-sm">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--primary)]" />
                      <span className="font-medium break-words">{material.name}</span>
                    </li>
                  ))}
                  {defaultMaterials.length >= 10 && (
                    <li className="text-muted-foreground pl-4 text-sm italic">... e possivelmente mais materiais</li>
                  )}
                </ul>
              </div>
            )}

            {currentMaterials.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold text-[var(--primary)]">
                  Materiais com esta campanha na agenda ({currentMaterials.length}):
                </h4>
                <ul className="space-y-2">
                  {currentMaterials.map((material) => (
                    <li key={material.uuid} className="flex items-center gap-2 pl-4 text-sm">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--primary)]" />
                      <span className="font-medium break-words">{material.name}</span>
                    </li>
                  ))}
                  {currentMaterials.length >= 10 && (
                    <li className="pl-4 text-sm italic">... e possivelmente mais materiais</li>
                  )}
                </ul>
                <p className="text-muted-foreground mt-3 pl-4 text-xs italic">
                  * Estes materiais retornarão automaticamente para suas respectivas campanhas padrão após a exclusão.
                </p>
              </div>
            )}
          </div>

          {/* Batch Update Card */}
          {defaultMaterials.length > 0 && (
            <div className="flex-shrink-0 px-6 pb-4">
              <Card className="rounded-lg border border-gray-200 shadow-sm">
                <CardContent className="pt-6">
                  <h3 className="mb-2 text-base font-semibold text-[var(--primary)]">
                    Alterar campanhas padrão em lote
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm">
                    Selecione uma nova campanha padrão para substituir a campanha padrão atual em todos os{' '}
                    {defaultMaterials.length}{' '}
                    {defaultMaterials.length === 1 ? 'material listado' : 'materiais listados'} acima. A campanha será
                    excluída automaticamente após a atualização.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium">Nova campanha padrão</label>
                      <Select value={selectedCampaignUuid} onValueChange={setSelectedCampaignUuid}>
                        <SelectTrigger className="w-full" disabled={isLoadingCampaigns || isLoading}>
                          <SelectValue placeholder="Selecione uma campanha..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCampaigns.length === 0 ? (
                            <div className="text-muted-foreground px-2 py-4 text-center text-sm">
                              {isLoadingCampaigns ? 'Carregando campanhas...' : 'Nenhuma campanha disponível'}
                            </div>
                          ) : (
                            availableCampaigns.map((campaign) => (
                              <SelectItem key={campaign.uuid} value={campaign.uuid}>
                                {campaign.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => onUpdateAndDelete?.(selectedCampaignUuid)}
                      disabled={!selectedCampaignUuid || isLoading || isLoadingCampaigns}
                      loading={isLoading}
                    >
                      Atualizar e Excluir Campanha
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="flex-shrink-0 p-6 pt-0">
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Render confirmation modal (can delete)
  const renderConfirmationModal = () => {
    const currentMaterials = dependencies?.currentMaterials || [];

    return (
      <Dialog key="confirmation-modal" open={isOpen} onOpenChange={onClose}>
        <DialogContent className="flex max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:max-h-[85vh] sm:max-w-5xl">
          <div className="flex-shrink-0 p-6 pb-4">
            <DialogHeader>
              <DialogTitle>Confirmar exclusão de campanha</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
          </div>

          {currentMaterials.length > 0 && (
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900 dark:bg-yellow-950">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-600 dark:text-yellow-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="mb-2 text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                      Impacto nos materiais ({currentMaterials.length})
                    </h4>
                    <p className="mb-3 text-xs text-yellow-700 dark:text-yellow-400">
                      Os seguintes materiais estão usando esta campanha atualmente. Após a exclusão, eles voltarão
                      automaticamente para suas campanhas padrão:
                    </p>
                    <ul className="space-y-1">
                      {currentMaterials.map((material) => (
                        <li
                          key={material.uuid}
                          className="flex items-center gap-2 pl-2 text-xs text-yellow-800 dark:text-yellow-300"
                        >
                          <span className="h-1 w-1 flex-shrink-0 rounded-full bg-yellow-600 dark:bg-yellow-400" />
                          <span className="break-words">{material.name}</span>
                        </li>
                      ))}
                      {currentMaterials.length >= 10 && (
                        <li className="pl-2 text-xs text-yellow-600 italic dark:text-yellow-500">
                          ... e possivelmente mais materiais
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex-shrink-0 p-6 pt-0">
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={onConfirm} loading={isLoading}>
                Excluir campanha
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Only render the appropriate modal based on canDelete state
  // This prevents the animation flicker when switching between modals
  if (!canDelete && dependencies) {
    return renderWarningModal();
  }

  if (canDelete) {
    return renderConfirmationModal();
  }

  return null;
}
